/**
 * Service Worker Registration
 * 
 * This module handles service worker registration for PWA functionality.
 * It provides:
 * - Offline caching for static assets
 * - Background sync for failed requests
 * - Push notification support (future)
 * - Update notifications
 */

// Check if service workers are supported
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

/**
 * Configuration for service worker
 */
const SW_CONFIG = {
  // Scope of the service worker
  scope: '/',
  // Update check interval (in ms)
  updateInterval: 60 * 60 * 1000, // 1 hour
  // Show update notification
  showUpdateNotification: true
};

/**
 * Register service worker
 * @param {Object} config - Registration config
 * @param {Function} config.onUpdate - Called when new content is available
 * @param {Function} config.onSuccess - Called when content is cached for offline
 * @param {Function} config.onError - Called on registration error
 */
export function register(config = {}) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    
    if (publicUrl.origin !== window.location.origin) {
      // Service worker won't work if PUBLIC_URL is on a different origin
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Running on localhost - check if SW exists
        checkValidServiceWorker(swUrl, config);
        
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service worker. ' +
            'To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Not localhost - register service worker
        registerValidSW(swUrl, config);
      }
    });

    // Periodic update check
    if (SW_CONFIG.updateInterval > 0) {
      setInterval(() => {
        checkForUpdates();
      }, SW_CONFIG.updateInterval);
    }
  }
}

/**
 * Register valid service worker
 */
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl, { scope: SW_CONFIG.scope })
    .then((registration) => {
      // Check for updates on page load
      registration.update();

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        
        if (installingWorker == null) {
          return;
        }
        
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content is available
              console.log(
                'New content is available and will be used when all tabs for this page are closed.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }

              // Show update notification
              if (SW_CONFIG.showUpdateNotification) {
                showUpdateNotification(registration);
              }
            } else {
              // Content is cached for offline use
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
      
      if (config && config.onError) {
        config.onError(error);
      }
    });
}

/**
 * Check if service worker is valid
 */
function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file
      const contentType = response.headers.get('content-type');
      
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found - reload the page
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found - proceed as normal
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

/**
 * Unregister service worker
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

/**
 * Check for service worker updates
 */
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.update();
    });
  }
}

/**
 * Skip waiting and activate new service worker
 */
export function skipWaiting(registration) {
  if (registration && registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Show update notification to user
 */
function showUpdateNotification(registration) {
  // Create notification element
  const notification = document.createElement('div');
  notification.id = 'sw-update-notification';
  notification.innerHTML = `
    <div class="sw-update-content">
      <span class="sw-update-message">A new version is available!</span>
      <div class="sw-update-actions">
        <button class="sw-update-button sw-update-refresh">Update Now</button>
        <button class="sw-update-button sw-update-dismiss">Later</button>
      </div>
    </div>
  `;
  
  // Add styles
  notification.style.cssText = `
    position: fixed;
    bottom: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: #1a237e;
    color: white;
    padding: 16px 24px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    animation: slideUp 0.3s ease;
  `;
  
  // Add button styles
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateX(-50%) translateY(20px); }
      to { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
    .sw-update-content {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .sw-update-message {
      font-weight: 500;
    }
    .sw-update-actions {
      display: flex;
      gap: 8px;
    }
    .sw-update-button {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      font-size: 14px;
      transition: background 0.2s;
    }
    .sw-update-refresh {
      background: #4fc3f7;
      color: #1a237e;
    }
    .sw-update-refresh:hover {
      background: #29b6f6;
    }
    .sw-update-dismiss {
      background: transparent;
      color: #b3e5fc;
      border: 1px solid #b3e5fc;
    }
    .sw-update-dismiss:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  `;
  
  document.head.appendChild(style);
  document.body.appendChild(notification);
  
  // Handle refresh click
  notification.querySelector('.sw-update-refresh').addEventListener('click', () => {
    skipWaiting(registration);
    window.location.reload();
  });
  
  // Handle dismiss click
  notification.querySelector('.sw-update-dismiss').addEventListener('click', () => {
    notification.remove();
  });
  
  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 30000);
}

/**
 * Request persistent storage
 * Returns true if granted, false otherwise
 */
export async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    const isPersisted = await navigator.storage.persist();
    console.log(`Persistent storage ${isPersisted ? 'granted' : 'denied'}`);
    return isPersisted;
  }
  return false;
}

/**
 * Get storage estimate
 * Returns usage and quota in bytes
 */
export async function getStorageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage,
      quota: estimate.quota,
      percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2)
    };
  }
  return null;
}

/**
 * Clear all caches
 */
export async function clearAllCaches() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    console.log('All caches cleared');
    return true;
  }
  return false;
}

const serviceWorkerUtils = {
  register,
  unregister,
  checkForUpdates,
  skipWaiting,
  requestPersistentStorage,
  getStorageEstimate,
  clearAllCaches
};

export default serviceWorkerUtils;
